"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { Target, Lock } from "lucide-react";
import DashboardCard from "./DashboardCard";
import DashboardCardLoading from "./DashboardCardLoading";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { hasFeatureAccess } from "@/lib/pass-utils";
import { PASS_FEATURES } from "@/types/pass";
import { getPassInfoFromMetadata } from "@/components/user-button-pages/helpers";

/** /api/study-progress 응답 중 전체현황에 필요한 최소 형태. */
type StudyData = {
  attendanceSummary: {
    theory: { attendedCount: number; totalPages: number };
    coding: { attendedCount: number; totalPages: number };
  };
  problems: {
    total: {
      totalCount: number;
      attempted: number;
      correct: number;
      incorrect: number;
      accuracyRate: number;
      attemptRate: number;
    };
  };
};

/**
 * 대시보드 "전체 현황" 위젯 (JCG-2073).
 * 공부 진행상황(StudyProgressPage)의 "전체" 탭 요약만 대시보드에 맞게 보여준다(제목/탭/이전 회차
 * 보기 없음). 데이터는 동일한 `/api/study-progress`, 접근 게이트는 Premium(StudyProgressPage와 동일).
 */
export default function DashboardStudyProgress({
  data: injectedData,
}: {
  /**
   * 지난 회차 스냅샷(JCG-3283) 주입값. 있으면 fetch를 스킵하고 이 값을 렌더한다.
   * 게이트(Premium)는 그대로 렌더 시점 현재 pass로 적용 — 비Premium은 스냅샷에서도 잠금 카드.
   */
  data?: StudyData;
} = {}) {
  const { user, isLoaded } = useUser();
  const passInfo = getPassInfoFromMetadata(user?.publicMetadata);
  const starterPurchasedAt =
    (user?.publicMetadata?.starterPurchasedAt as string | null) ?? null;
  const hasAccess = hasFeatureAccess(
    passInfo.passType,
    PASS_FEATURES.studyProgress,
    starterPurchasedAt,
  );

  const [fetchedData, setFetchedData] = useState<StudyData | null>(null);
  // 주입 모드면 로딩 없이 즉시 표시. 라이브 모드는 무조건 로딩부터 시작 — Clerk 로드 전엔
  // hasAccess가 false(user undefined)라, loading을 hasAccess로 초기화하면 유료 사용자가
  // "잠김 → 기록 없음 → 데이터"로 깜빡인다(코드리뷰 #1).
  const [loading, setLoading] = useState(injectedData === undefined);
  const data = injectedData ?? fetchedData;

  useEffect(() => {
    if (injectedData !== undefined) return; // 스냅샷 주입 모드: fetch 스킵(loading 이미 false)
    if (!isLoaded) return; // Clerk 로드 전엔 로딩 유지
    let alive = true;
    // 접근 권한 없으면 fetch 불필요. async IIFE 안에서 처리해 set-state-in-effect 룰 준수.
    (async () => {
      if (!hasAccess) {
        if (alive) setLoading(false);
        return;
      }
      try {
        const res = await fetch("/api/study-progress", { cache: "no-store" });
        if (res.ok) {
          const d = await res.json();
          if (alive && d.success) setFetchedData(d);
        }
      } catch (e) {
        console.error("대시보드 진행상황 로드 오류:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isLoaded, hasAccess, injectedData]);

  const theoryAttended = data?.attendanceSummary.theory.attendedCount ?? 0;
  const theoryTotal = data?.attendanceSummary.theory.totalPages ?? 0;
  const codingAttended = data?.attendanceSummary.coding.attendedCount ?? 0;
  const codingTotal = data?.attendanceSummary.coding.totalPages ?? 0;
  const p = data?.problems.total;

  return (
    <DashboardCard icon={Target} title="전체 현황" href="/my/study-progress">
      {!isLoaded || loading ? (
        <DashboardCardLoading height={220} label="진행상황 불러오는 중…" />
      ) : !hasAccess ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <Lock className="h-7 w-7 text-amber-500" aria-hidden />
          <p className="text-sm text-muted-foreground">
            공부 진행상황은 Premium 이상 이용권에서 볼 수 있어요.
          </p>
          <Link href="/membership" className="mt-1">
            <Button size="sm">이용권 업그레이드</Button>
          </Link>
        </div>
      ) : p ? (
        <div className="space-y-4">
          {/* 이론 출석 */}
          <ProgressRow
            label="이론 출석"
            value={`${theoryAttended} / ${theoryTotal} 페이지`}
            percent={theoryTotal > 0 ? (theoryAttended / theoryTotal) * 100 : 0}
          />
          {/* 코딩 출석 */}
          <ProgressRow
            label="코딩 출석"
            value={`${codingAttended} / ${codingTotal} 페이지`}
            percent={codingTotal > 0 ? (codingAttended / codingTotal) * 100 : 0}
          />
          {/* 문제 시도 */}
          <ProgressRow
            label="문제 시도"
            value={`${p.attempted} / ${p.totalCount}문제 (${p.attemptRate.toFixed(0)}%)`}
            percent={p.attemptRate}
          />
          {/* 시도 중 정답률 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">시도 중 정답률</span>
            <span
              className={cn(
                "text-sm font-bold",
                p.accuracyRate >= 70
                  ? "text-green-600 dark:text-green-400"
                  : p.accuracyRate >= 50
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-red-600 dark:text-red-400",
              )}
            >
              {p.attempted > 0 ? p.accuracyRate.toFixed(1) : 0}%
            </span>
          </div>
          {/* 시도/정답/오답 */}
          <div className="grid grid-cols-3 gap-2 pt-1 text-center text-xs">
            <StatBox label="시도" value={p.attempted} tone="blue" />
            <StatBox label="정답" value={p.correct} tone="green" />
            <StatBox label="오답" value={p.incorrect} tone="red" />
          </div>
        </div>
      ) : (
        <p className="py-10 text-center text-sm text-muted-foreground">
          아직 학습 기록이 없어요.
        </p>
      )}
    </DashboardCard>
  );
}

function ProgressRow({
  label,
  value,
  percent,
}: {
  label: string;
  value: string;
  percent: number;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm font-medium text-foreground">{value}</span>
      </div>
      <Progress value={percent} className="h-2" />
    </div>
  );
}

const TONE: Record<string, string> = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300",
  green: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300",
  red: "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300",
};

function StatBox({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "blue" | "green" | "red";
}) {
  return (
    <div className={cn("rounded-lg p-2", TONE[tone])}>
      <p>{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
